import React, { useEffect, useState } from "react";
import { useUserData } from "../../../hooks/useUserData";
import InfoAlert from "../../common/InfoAlert";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { API_CONFIG, getApiUrl } from "../../../config/apiconfig";
import { loginUser, verifyMfaLogin } from "../../../services/apiService";
import MfaMethodChoice from "../../Pages/MfaMethodChoice";
import MfaVerification from "../../Pages/MfaVerification";
import MfaLoginAuthenticatorSetup from "../../Pages/MfaLoginAuthenticatorSetup";

const AuthModal = ({
  show,
  onClose,
  onSubmit,
  showDescription = false,
  requireDescription = false,
  useMfa = false,
  descriptionLabel = "Description / reason",
  descriptionPlaceholder = "Description…",
  descriptionMaxLength = 0,
  introMessage = null,
}) => {
  const { userData } = useUserData();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [description, setDescription] = useState("");
  const [codes, setCodes] = useState(["", "", ""]);
  const [placeholders, setPlaceholders] = useState([]);
  const [authStep, setAuthStep] = useState("credentials");
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [pendingAuthPayload, setPendingAuthPayload] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState("");

  const getOrdinal = (n) => {
    if (n === 1) return "st";
    if (n === 2) return "nd";
    if (n === 3) return "rd";
    return "th";
  };

  const generateNewPlaceholders = () => {
    const digits = [];
    while (digits.length < 3) {
      const d = Math.floor(Math.random() * 6) + 1;
      if (!digits.includes(d)) digits.push(d);
    }
    return digits;
  };

  const resetMfaState = () => {
    setAuthStep("credentials");
    setMfaChallenge(null);
    setPendingAuthPayload(null);
    setMfaLoading(false);
    setMfaError("");
  };

  useEffect(() => {
    if (!show) return;
    setPlaceholders(generateNewPlaceholders());
    resetMfaState();
  }, [show]);

  if (!show) return null;

  const handleCode = (i, val) => {
    if (!/^[0-9]?$/.test(val)) return;

    const temp = [...codes];
    temp[i] = val;
    setCodes(temp);

    if (val && i < 2) {
      const next = document.getElementById(`auth-code_${i + 1}`);
      next?.focus();
    }
  };

  const apiCall = async (url, options = {}) => {
    const config = {
      method: "GET",
      headers: API_CONFIG.headers,
      timeout: API_CONFIG.timeout,
      ...options,
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("Request timeout");
      }
      throw error;
    }
  };

  const reasonMaxLength = Number(descriptionMaxLength) > 0
    ? Number(descriptionMaxLength)
    : 0;
  const showDescriptionField = showDescription || requireDescription;

  const handleDescriptionChange = (event) => {
    const next = String(event.target.value || "");
    setDescription(
      reasonMaxLength > 0 ? next.slice(0, reasonMaxLength) : next,
    );
  };

  const buildAuthPayload = () => ({
    Password: password,
    AccessCodeIndex0: placeholders[0],
    AccessCodeIndex1: placeholders[1],
    AccessCodeIndex2: placeholders[2],
    AccessCodeValue0: codes[0],
    AccessCodeValue1: codes[1],
    AccessCodeValue2: codes[2],
    EmailId: userData.EmailId,
    Description: description,
  });

  const completeAuth = (payload) => {
    clearAuthForm();
    resetMfaState();
    onSubmit({ ...payload, Success: 1, success: 1 });
  };

  const clearAuthForm = () => {
    setCodes(["", "", ""]);
    setPlaceholders(generateNewPlaceholders());
    setPassword("");
    setDescription("");
  };

  const clearAccessCodeOnly = () => {
    setCodes(["", "", ""]);
    setPlaceholders(generateNewPlaceholders());
  };

  const authenticateWithLegacyLogin = async (payload) => {
    const url = getApiUrl("login");
    const response = await apiCall(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    payload.Success = response.success;
    if (response.success === 0) {
      showAlert({
        title: "Error",
        message: response.message,
        type: "danger",
      });
      clearAccessCodeOnly();
      return;
    }

    completeAuth(payload);
  };

  const authenticateWithMfaLogin = async (payload) => {
    setMfaLoading(true);
    setMfaError("");
    try {
      const response = await loginUser({
        email: userData.EmailId,
        password,
        accessCodeIndexes: placeholders,
        accessCodeValues: codes,
        forReauth: true,
        organizationId: userData.OrganizationId || userData.Organizationid || "",
      });

      if (response.success === 2 && response.data) {
        setPendingAuthPayload(payload);
        setMfaChallenge(response.data);
        const requiresMethodSelection =
          response.data.RequiresMethodSelection === true ||
          response.data.requiresMethodSelection === true;
        setAuthStep(requiresMethodSelection ? "mfaChoose" : "mfa");
        return;
      }

      if (response.success !== 1) {
        showAlert({
          title: "Error",
          message: response.message || "Authentication failed",
          type: "danger",
        });
        clearAccessCodeOnly();
        return;
      }

      completeAuth(payload);
    } catch (error) {
      console.error("Error authenticating user:", error);
      showAlert({
        title: "Error",
        message: error?.message || "Authentication failed",
        type: "danger",
      });
      clearAccessCodeOnly();
    } finally {
      setMfaLoading(false);
    }
  };

  const handleOK = async () => {
    if (!password || codes.some((x) => x === "")) {
      showAlert({
        title: "Error",
        message: "Please fill all the fields",
        type: "danger",
      });
      return;
    }

    if (requireDescription && !String(description || "").trim()) {
      showAlert({
        title: "Error",
        message: "Please enter a description (reason).",
        type: "danger",
      });
      return;
    }

    const payload = buildAuthPayload();

    try {
      if (useMfa) {
        await authenticateWithMfaLogin(payload);
      } else {
        await authenticateWithLegacyLogin(payload);
      }
    } catch (error) {
      console.error("Error authenticating user:", error);
      showAlert({
        title: "Error",
        message: error?.message || "Authentication failed",
        type: "danger",
      });
      clearAccessCodeOnly();
    }
  };

  const handleMfaVerify = async (otpCode) => {
    if (!pendingAuthPayload) return;
    setMfaLoading(true);
    setMfaError("");
    try {
      const token =
        mfaChallenge?.MfaSessionToken ?? mfaChallenge?.mfaSessionToken;
      const response = await verifyMfaLogin(token, otpCode);
      if (response.success !== 1) {
        setMfaError(response.message || "Invalid verification code.");
        return;
      }
      completeAuth(pendingAuthPayload);
    } catch (error) {
      setMfaError("Verification failed. Please try again.");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaBack = () => {
    resetMfaState();
    clearAccessCodeOnly();
  };

  const handleMfaMethodSelected = (updatedChallenge) => {
    setMfaChallenge(updatedChallenge);
    setAuthStep("mfa");
    setMfaError("");
  };

  const handleMfaSetupRequired = (updatedChallenge) => {
    setMfaChallenge(updatedChallenge);
    setAuthStep("mfaAuthSetup");
    setMfaError("");
  };

  const handleMfaSetupComplete = (updatedChallenge) => {
    setMfaChallenge(updatedChallenge);
    setAuthStep("mfa");
    setMfaError("");
  };

  const modalTitle =
    authStep === "credentials"
      ? useMfa
        ? "Authenticate to continue"
        : "Password Authentication"
      : "Verify your identity";

  const renderBody = () => {
    if (authStep === "mfaChoose" && mfaChallenge) {
      return (
        <MfaMethodChoice
          challenge={mfaChallenge}
          onSelected={handleMfaMethodSelected}
          onSetupRequired={handleMfaSetupRequired}
          onBack={handleMfaBack}
          loading={mfaLoading}
        />
      );
    }

    if (authStep === "mfaAuthSetup" && mfaChallenge) {
      return (
        <MfaLoginAuthenticatorSetup
          challenge={mfaChallenge}
          onComplete={handleMfaSetupComplete}
          onBack={() => setAuthStep("mfaChoose")}
        />
      );
    }

    if (authStep === "mfa" && mfaChallenge) {
      return (
        <MfaVerification
          challenge={mfaChallenge}
          onVerify={handleMfaVerify}
          onBack={handleMfaBack}
          loading={mfaLoading}
          error={mfaError}
          onChallengeUpdate={setMfaChallenge}
          onSetupRequired={handleMfaSetupRequired}
        />
      );
    }

    return (
      <>
        {useMfa ? (
          <p className="small text-muted mb-3">
            {introMessage ||
              "Confirm your password and access code to continue."}
          </p>
        ) : null}
        <form
          id="auth-modal-form"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            handleOK();
          }}
        >
          <input type="text" style={{ display: "none" }} />
          <input type="password" style={{ display: "none" }} />

          <div className="position-relative mb-3">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control pe-5"
              placeholder="Enter Password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={mfaLoading}
            />
            <button
              type="button"
              className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-secondary p-0 me-2"
              style={{ minWidth: "2rem" }}
              onClick={() => setShowPassword((prev) => !prev)}
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
            </button>
          </div>

          <label className="small text-muted">Enter Access Code</label>
          <div className="d-flex gap-2 mt-1">
            {placeholders.map((p, i) => (
              <div key={i} className="position-relative flex-grow-1">
                <input
                  id={`auth-code_${i}`}
                  className="form-control text-center"
                  placeholder={p}
                  maxLength={1}
                  autoComplete="off"
                  value={codes[i]}
                  onChange={(e) => handleCode(i, e.target.value)}
                  disabled={mfaLoading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && i === 2) {
                      e.preventDefault();
                      handleOK();
                    }
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "3.5rem",
                    transform: "translateY(-50%)",
                    fontSize: "0.8rem",
                    color: "#999",
                    pointerEvents: "none",
                  }}
                >
                  {getOrdinal(p)}
                </span>
              </div>
            ))}
          </div>

          {showDescriptionField && (
            <div className="mt-3">
              <label className="form-label small mb-1">
                {descriptionLabel}
                {requireDescription && (
                  <span className="text-danger"> *</span>
                )}
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder={
                  requireDescription
                    ? descriptionPlaceholder
                    : descriptionPlaceholder || "Description…"
                }
                value={description}
                onChange={handleDescriptionChange}
                maxLength={reasonMaxLength > 0 ? reasonMaxLength : undefined}
                required={requireDescription}
                disabled={mfaLoading}
              />
              {reasonMaxLength > 0 ? (
                <div className="d-flex justify-content-end mt-1">
                  <small className="text-muted">
                    {description.length}/{reasonMaxLength}
                  </small>
                </div>
              ) : null}
            </div>
          )}
        </form>
      </>
    );
  };

  return (
    <div className="modal show d-block" style={styles.overlay}>
      <div
        className={`modal-dialog modal-dialog-centered${
          authStep !== "credentials" ? " modal-lg" : ""
        }`}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{modalTitle}</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">{renderBody()}</div>

          {authStep === "credentials" ? (
            <div className="modal-footer">
              <button
                type="submit"
                className="btn btn-primary"
                form="auth-modal-form"
                disabled={mfaLoading}
              >
                {mfaLoading ? "Verifying…" : useMfa ? "Continue" : "OK"}
              </button>
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </div>
  );
};

const styles = {
  overlay: {
    background: "rgba(0,0,0,0.5)",
    position: "fixed",
    inset: 0,
    zIndex: 9999,
  },
};

export default AuthModal;
